import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  addEdge,
  Background,
  Connection,
  ConnectionMode,
  Controls,
  Edge,
  Node,
  useEdgesState,
  useNodesState,
  XYPosition,
  NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './theme.css'; // ReactFlow theme overrides for light/dark mode
import '@radix-ui/themes/styles.css';
import { Box, Button, Flex, Text } from '@radix-ui/themes';

import * as Types from '@/types';
import { ValueType } from '@/lib/workflow-defs';
import { useCreateWorkflowMutation } from '@/graphql/mutations/CreateWorkflow.generated';
import { useCreateWorkflowNodeMutation } from '@/graphql/mutations/CreateWorkflowNode.generated';
import { useCreateWorkflowEdgeMutation } from '@/graphql/mutations/CreateWorkflowEdge.generated';
import { useCreateSignalMutation } from '@/graphql/mutations/CreateSignal.generated';

import NodePalette from './NodesPanel';
import { SignalNode } from './nodes/signals';
import { ActionNode } from './nodes/actions';
import { TransformerNode } from './nodes/transformers';
import { FilterNode } from './nodes/filters';

interface WorkflowCanvasProps {
  workflow: Types.Workflow;
  onRefetch?: () => void;
}

// Creates the default *WorkflowGraphNode* structure for a new node dropped on the canvas
const getDefaultNodeData = (
  nodeType: Types.WorkflowNodeType,
  variant: string,
): Types.WorkflowNode => {
  let defaultNode: Types.WorkflowNode = {
    id: crypto.randomUUID(),
    position: { x: 0, y: 0 },
    styleData: {},
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  switch (nodeType) {
    case Types.WorkflowNodeType.Signal:
      defaultNode = {
        ...defaultNode,
        signal: {
          id: crypto.randomUUID(),
          name: 'New Signal',
          signalType: undefined as any, // User must select signal type before saving
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          fingerprints: [],
        },
        controlFlows: [
          {
            id: crypto.randomUUID(),
            key: 'next',
            direction: Types.SlotDirection.Output,
            slotType: Types.SlotType.Control,
            nodeId: defaultNode.id,
            optional: false,
            dataType: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ],
      };
      break;
    case Types.WorkflowNodeType.Transformer:
      defaultNode = {
        ...defaultNode,
        transformer: {
          id: crypto.randomUUID(),
          name: 'New Transformer',
          transformerType: variant as Types.TransformerType,
          fromUnit: ValueType.USD,
          toUnit: ValueType.AUD,
          inputSchema: {
            type: 'object',
            properties: {
              value: {
                type: 'number',
              },
            },
          },
          outputSchema: {
            type: 'object',
            properties: {
              value: {
                type: 'number',
              },
            },
          },
        },
      };
      break;
    case Types.WorkflowNodeType.Enricher:
      defaultNode = {
        ...defaultNode,
        // enricher: {
        //   id: crypto.randomUUID(),
        //   name: "New Enricher",
        //   createdAt: new Date().toISOString(),
        //   updatedAt: new Date().toISOString(),
        // },
      };
      break;
    case Types.WorkflowNodeType.Filter:
      defaultNode = {
        ...defaultNode,
        filter: {
          id: crypto.randomUUID(),
          name: 'New Filter',
          filterType: 'default',
          conditions: [
            {
              field: {
                type: 'VARIABLE',
                outputKey: 'value',
              },
              operation: 'EQUALS',
              value: {
                type: 'LITERAL',
                value: 100,
              },
            },
          ],
        },
        controlFlows: [
          {
            key: 'trigger',
            dataType: Types.SlotDataType.Control,
            direction: Types.SlotDirection.Output,
            optional: true,
            createdAt: new Date().toISOString(),
            id: crypto.randomUUID(),
            nodeId: defaultNode.id,
            updatedAt: new Date().toISOString(),
          },
        ],
      };
      break;
    case Types.WorkflowNodeType.Delay:
      defaultNode = {
        ...defaultNode,
        // delay: {
        //   id: crypto.randomUUID(),
        //   name: "New Delay",
        //   createdAt: new Date().toISOString(),
        //   updatedAt: new Date().toISOString(),
        // },
      };
      break;
    case Types.WorkflowNodeType.Merge:
      defaultNode = {
        ...defaultNode,
        // merge: {
        //   id: crypto.randomUUID(),
        //   name: "New Merge",
        //   createdAt: new Date().toISOString(),
        //   updatedAt: new Date().toISOString(),
        // },
      };
      break;
    case Types.WorkflowNodeType.Action:
      defaultNode = {
        ...defaultNode,
        action: {
          id: crypto.randomUUID(),
          name: 'New Action',
          actionType: variant as Types.ActionType,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      };
      break;
    case Types.WorkflowNodeType.Workflow:
      defaultNode = {
        ...defaultNode,
        // workflow: {
        //   id: crypto.randomUUID(),
        //   name: "New Workflow",
        //   createdAt: new Date().toISOString(),
        //   updatedAt: new Date().toISOString(),
        //   enabled: true,
        // },
      };
      break;
    default:
      throw new Error(
        `Unsupported node type (${nodeType}) and variant (${variant})`,
      );
  }

  return defaultNode;
};

// Helper function to get WorkflowNodeType enum from string
export const getNodeType = (nodeType: string): Types.WorkflowNodeType => {
  switch (nodeType) {
    case 'SignalNode':
      return Types.WorkflowNodeType.Signal;
    case 'TransformerNode':
      return Types.WorkflowNodeType.Transformer;
    case 'EnricherNode':
      return Types.WorkflowNodeType.Enricher;
    case 'FilterNode':
      return Types.WorkflowNodeType.Filter;
    case 'DelayNode':
      return Types.WorkflowNodeType.Delay;
    case 'MergeNode':
      return Types.WorkflowNodeType.Merge;
    case 'ActionNode':
      return Types.WorkflowNodeType.Action;
    case 'SubWorkflowNode':
      return Types.WorkflowNodeType.Workflow;
    default:
      throw new Error(`Unsupported node type string: ${nodeType}`);
  }
};

function WorkflowCanvas({ workflow, onRefetch }: WorkflowCanvasProps) {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [workflowId, setWorkflowId] = useState<string | undefined>(
    workflow?.id,
  );
  const [createWorkflow] = useCreateWorkflowMutation();
  const [createWorkflowNode] = useCreateWorkflowNodeMutation();
  const [createWorkflowEdge] = useCreateWorkflowEdgeMutation();
  const [createSignal] = useCreateSignalMutation();

  // Debug: log workflow object on mount
  React.useEffect(() => {
    console.log('Canvas mounted - Workflow object:', workflow);
    console.log('Canvas mounted - Workflow ID:', workflow?.id);
    console.log(
      'Canvas mounted - Workflow keys:',
      workflow ? Object.keys(workflow) : 'workflow is undefined',
    );
  }, [workflow]);

  // const initialEdges =
  const initialNodes =
    workflow?.graphNodes?.edges?.map(({ node }) => ({
      id: node.id,
      type: getNodeType(node.__typename),
      position: node.position,
      data: node,
    })) || [];

  const initialEdges =
    workflow?.graphEdges?.edges?.map((edge) => ({
      id: edge.node.id,
      source: edge.node.sourceId,
      target: edge.node.targetId,
      sourceHandle: edge.node.sourceHandle,
      targetHandle: edge.node.targetHandle,
    })) || [];

  // Ensure initialNodes/Edges are arrays, default to empty if not
  const [nodes, setNodes, onNodesChange] = useNodesState(
    Array.isArray(initialNodes) ? initialNodes : [],
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    Array.isArray(initialEdges) ? initialEdges : [],
  );

  const onConnect = useCallback(
    (params: Edge | Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = document
        .querySelector('.react-flow')
        ?.getBoundingClientRect();
      const transferData = event.dataTransfer.getData('application/reactflow');
      const [typeStr, variantStr] = transferData.split(':');
      console.log('transferData', transferData);
      console.log('typeStr', typeStr);
      console.log('variantStr', variantStr);
      console.log('reactFlowBounds', reactFlowBounds);

      // Validate data
      if (!typeStr || !variantStr || !reactFlowBounds) {
        console.error('Invalid drop data or bounds', {
          typeStr,
          variantStr,
          reactFlowBounds,
        });
        return;
      }

      let type: Types.WorkflowNodeType;
      try {
        type = getNodeType(typeStr);
      } catch (e) {
        console.error(e);
        return;
      }
      const variant: string = variantStr; // Keep as string for getDefaultNodeData
      console.log('type', type);
      console.log('variant', variant);
      const position: XYPosition = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };

      // Create the base WorkflowGraphNode structure
      const defaultNodeData = getDefaultNodeData(type, variant);

      // Create the ReactFlow Node object
      const newNode: Node<Types.WorkflowNode> = {
        // Use a temporary but unique ID for ReactFlow internal handling
        id: defaultNodeData.id, // Use the generated UUID as the ReactFlow key
        type: type, // Use the WorkflowNodeType enum value
        position,
        data: {
          // The 'data' prop holds the full WorkflowGraphNode
          ...defaultNodeData,
          position, // Update position in the data object as well
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [nodes, setNodes], // Ensure nodes is a dependency if using nodes.length for ID generation (though UUID is better)
  );

  const onDeleteNode = useCallback(
    (id: string) => {
      setNodes((nds) => nds.filter((node) => node.id !== id));
      setEdges((eds) =>
        eds.filter((edge) => edge.source !== id && edge.target !== id),
      );
    },
    [setNodes, setEdges],
  );

  const onUpdateNode = useCallback(
    (id: string, updatedData: Partial<Types.WorkflowNode>) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                ...updatedData,
                updatedAt: new Date().toISOString(),
              } as Types.WorkflowNode,
            };
          }
          return node;
        }),
      );
    },
    [setNodes],
  );

  // Map WorkflowNodeTypes to React Components
  const nodeTypes = useMemo(
    () => ({
      [Types.WorkflowNodeType.Signal]: (props: NodeProps<Types.SignalNode>) => (
        <SignalNode
          {...props}
          onDelete={onDeleteNode}
          onChange={onUpdateNode}
        />
      ),
      [Types.WorkflowNodeType.Transformer]: (
        props: NodeProps<Types.TransformerNode>,
      ) => (
        <TransformerNode
          {...props}
          onDelete={onDeleteNode}
          onChange={onUpdateNode}
        />
      ),
      [Types.WorkflowNodeType.Enricher]: (
        props: NodeProps<Types.EnricherNode>,
      ) => <>TODO: Enricher</>,
      [Types.WorkflowNodeType.Filter]: (props: NodeProps<Types.FilterNode>) => (
        <FilterNode
          {...props}
          onDelete={onDeleteNode}
          onChange={onUpdateNode}
        />
      ),
      [Types.WorkflowNodeType.Delay]: (props: NodeProps<Types.DelayNode>) => (
        <>TODO: Delay</>
      ),
      [Types.WorkflowNodeType.Merge]: (props: NodeProps<Types.MergeNode>) => (
        <>TODO: Merge</>
      ),
      [Types.WorkflowNodeType.Action]: (props: NodeProps<Types.ActionNode>) => (
        <ActionNode
          {...props}
          onDelete={onDeleteNode}
          onChange={onUpdateNode}
        />
      ),
      [Types.WorkflowNodeType.Workflow]: (
        props: NodeProps<Types.SubWorkflowNode>,
      ) => <>TODO: Workflow</>,
    }),
    [onDeleteNode, onUpdateNode],
  );

  const handleSaveWorkflow = useCallback(async () => {
    setIsSaving(true);
    try {
      let currentWorkflowId = workflowId;

      // If workflow doesn't have an ID, create it first
      if (!currentWorkflowId) {
        console.log('Creating new workflow...');
        const workflowResult = await createWorkflow({
          variables: {
            input: {
              name: 'New Workflow',
              description: null,
              viewportState: { x: 0, y: 0, zoom: 1 },
              enabled: false,
            },
          },
        });

        if (!workflowResult.data?.createWorkflow?.id) {
          throw new Error('Failed to create workflow');
        }

        currentWorkflowId = workflowResult.data.createWorkflow.id;
        setWorkflowId(currentWorkflowId);
        console.log('Created workflow with ID:', currentWorkflowId);
      } else {
        console.log('Saving to existing workflow:', currentWorkflowId);
      }

      // Get already persisted node IDs from the workflow
      const persistedNodeIds = new Set(
        workflow.graphNodes.edges.map((edge) => edge.node.id),
      );

      // Get already persisted edge IDs
      const persistedEdgeIds = new Set(
        workflow.graphEdges.edges.map((edge) => edge.node.id),
      );

      // Find nodes that need to be saved (new nodes not in the backend)
      const nodesToSave = nodes.filter(
        (node) => !persistedNodeIds.has(node.id),
      );

      // Validate that all Signal nodes have a signalType selected
      const invalidSignals = nodesToSave.filter(
        (node) =>
          node.type === Types.WorkflowNodeType.Signal &&
          !node.data.signal?.signalType,
      );
      if (invalidSignals.length > 0) {
        alert(
          'Please select a Signal Type for all Signal nodes before saving.',
        );
        return;
      }

      // Save new nodes
      const nodeIdMap = new Map<string, string>(); // temp ID -> persisted ID
      for (const node of nodesToSave) {
        const nodeType = node.type as Types.WorkflowNodeType;
        let entityId: string;

        // For Signal nodes, create the signal first
        if (nodeType === Types.WorkflowNodeType.Signal && node.data.signal) {
          console.log('Creating signal first...');
          const signalResult = await createSignal({
            variables: {
              input: {
                name: node.data.signal.name,
                signalType: node.data.signal.signalType,
              },
            },
          });

          if (!signalResult.data?.createSignal?.id) {
            throw new Error('Failed to create signal');
          }

          entityId = signalResult.data.createSignal.id;
          console.log('Created signal with ID:', entityId);
        } else {
          // For other node types, use the existing ID or generate a new one
          entityId =
            node.data.action?.id ||
            node.data.transformer?.id ||
            crypto.randomUUID();
        }

        const input = {
          workflowId: currentWorkflowId,
          nodeType,
          label:
            node.data.signal?.name ||
            node.data.action?.name ||
            node.data.transformer?.name ||
            null,
          position: node.position,
          styleData: node.data.styleData || {},
          nodeId: entityId,
        };

        console.log('Creating node with input:', input);

        const result = await createWorkflowNode({
          variables: {
            input,
          },
        });

        // Map the temporary ID to the persisted ID
        if (result.data?.createWorkflowNode) {
          nodeIdMap.set(node.id, result.data.createWorkflowNode.id);
        }
      }

      // Find edges that need to be saved
      const edgesToSave = edges.filter(
        (edge) => !persistedEdgeIds.has(edge.id),
      );

      // Save new edges (using the persisted node IDs)
      for (const edge of edgesToSave) {
        const sourceId = nodeIdMap.get(edge.source) || edge.source;
        const targetId = nodeIdMap.get(edge.target) || edge.target;

        await createWorkflowEdge({
          variables: {
            input: {
              workflowId: currentWorkflowId,
              sourceNodeId: sourceId,
              targetNodeId: targetId,
              sourceHandle: edge.sourceHandle || null,
              targetHandle: edge.targetHandle || null,
              edgeType: null,
            },
          },
        });
      }

      // Refetch the workflow to get the updated state
      if (onRefetch) {
        onRefetch();
      }

      alert('Workflow saved successfully!');
    } catch (error) {
      console.error('Failed to save workflow:', error);
      alert('Failed to save workflow. See console for details.');
    } finally {
      setIsSaving(false);
    }
  }, [
    nodes,
    edges,
    workflow,
    workflowId,
    createWorkflow,
    createWorkflowNode,
    createWorkflowEdge,
    createSignal,
    onRefetch,
  ]);

  return (
    <Flex direction="row" style={{ height: '100vh' }}>
      <NodePalette />
      <div style={{ flexGrow: 1, backgroundColor: 'var(--gray-1)' }}>
        <Box
          px="4"
          py="3"
          style={{
            borderBottom: '1px solid var(--gray-6)',
            backgroundColor: 'var(--gray-2)',
          }}
        >
          <Flex align="center" justify="between" gap="2">
            <Text size="3" weight="bold" color="gray">
              {workflow?.name || 'Untitled Workflow'}
            </Text>
            <Flex direction="row" gap="4">
              <Flex gap="2">
                <Button
                  variant="soft"
                  size="1"
                  onClick={() => navigate('/workflows/create')}
                >
                  New
                </Button>
                <Button
                  variant="soft"
                  size="1"
                  onClick={handleSaveWorkflow}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="soft" size="1" color="green">
                  Validate
                </Button>
              </Flex>
              <Flex gap="2">
                <Button
                  variant="soft"
                  size="1"
                  onClick={() => navigate('runs')}
                >
                  View Runs
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Box>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDragOver={onDragOver}
          onDrop={onDrop}
          nodeTypes={nodeTypes}
          connectionMode={ConnectionMode.Loose}
          fitView
        >
          <Background />
          <Controls />
        </ReactFlow>
      </div>
    </Flex>
  );
}

export default WorkflowCanvas;
